import { updateFilteredCategories } from '../../../../core/utils/filterHelpers';
import { getDefaultRequestTagCodesForCategoryType } from '../../../../core/utils/requestTagCodes';
import { createStore } from '../../../../core/utils/stateManagers';
import { createSearchResultBucketKey } from '../../../utils/searchResultBucket';
import type { SelectItem, ServiceType } from '../../../../core/types';

const PRECEDENT_CATEGORY_TYPE = 'PRECEDENT';
const DECISIONS_AND_INTERPRETATIONS_CATEGORY_TYPE = 'DECISIONS_AND_INTERPRETATIONS';
const PRECEDENT_VIEW_CODE = 'precedent';
const DECISIONS_AND_INTERPRETATIONS_VIEW_CODE = 'decisions_and_interpretations';

// 카테고리 스토어 상태 인터페이스
export interface CategoryState {
    filteredCategories: AistudioAiCategoryWithTagDto[];
    allCategoriesFromInitialData: AistudioAiCategoryWithTagDto[];
    selectedTagsByTab: Map<string, { value: string; label: string }[]>;
    categoryTotalsMap: Map<string, number>;
    tabCounts: Map<string, number>;
    // 초기 검색 시 background prefetch 중인 탭 코드 집합
    // - 설정되어 있으면 해당 탭은 카운트 대신 spinner 로 표시된다.
    pendingTabCodes: Set<string>;
}

// 초기 상태
const initialState: CategoryState = {
    filteredCategories: [],
    allCategoriesFromInitialData: [],
    selectedTagsByTab: new Map(),
    categoryTotalsMap: new Map(),
    tabCounts: new Map(),
    pendingTabCodes: new Set(),
};

// 카테고리 스토어 생성
export const createCategoryStore = () => {
    const store = createStore<CategoryState>(initialState);

    const getDefaultRequestTagCodesForViewCode = (viewCode: string, categoryType?: string): string[] => {
        if (viewCode === PRECEDENT_VIEW_CODE) {
            return getDefaultRequestTagCodesForCategoryType(PRECEDENT_CATEGORY_TYPE);
        }

        if (viewCode === DECISIONS_AND_INTERPRETATIONS_VIEW_CODE) {
            return getDefaultRequestTagCodesForCategoryType(DECISIONS_AND_INTERPRETATIONS_CATEGORY_TYPE);
        }

        return getDefaultRequestTagCodesForCategoryType(categoryType);
    };

    const hasExactPrecedentCategory = (serviceType: ServiceType): boolean => {
        const state = store.getState();
        return state.filteredCategories.some(category => category.serviceType === serviceType && category.categoryType === PRECEDENT_CATEGORY_TYPE);
    };

    const isExactMatch = (cat: AistudioAiCategoryWithTagDto, view: { code: string; filters: { serviceType: ServiceType; categoryType?: string } }): boolean => {
        return cat.serviceType === view.filters.serviceType && (!view.filters.categoryType || cat.categoryType === view.filters.categoryType);
    };

    const isPrecedentSharedCategoryFallback = (cat: AistudioAiCategoryWithTagDto, view: { code: string; filters: { serviceType: ServiceType; categoryType?: string } }): boolean => {
        return (
            view.code === PRECEDENT_VIEW_CODE &&
            view.filters.serviceType === cat.serviceType &&
            view.filters.categoryType === PRECEDENT_CATEGORY_TYPE &&
            cat.categoryType === DECISIONS_AND_INTERPRETATIONS_CATEGORY_TYPE &&
            !hasExactPrecedentCategory(view.filters.serviceType)
        );
    };

    const matchesView = (cat: AistudioAiCategoryWithTagDto, view: { code: string; filters: { serviceType: ServiceType; categoryType?: string } }): boolean => {
        return isExactMatch(cat, view) || isPrecedentSharedCategoryFallback(cat, view);
    };

    const generateApiCategoriesForView = (viewCode: string, availableViews: Array<{ code: string; filters: { serviceType: ServiceType; categoryType?: string } }>) => {
        const state = store.getState();
        return state.filteredCategories
            .filter(cat => {
                if (viewCode === 'all') return true;
                const view = availableViews.find(v => v.code === viewCode);
                return view && matchesView(cat, view);
            })
            .flatMap(cat => {
                const matchedViewCodes = viewCode === 'all' ? availableViews.filter(view => matchesView(cat, view)).map(view => view.code) : [viewCode];

                return matchedViewCodes.map(matchedViewCode => ({
                    categoryId: String(cat.id),
                    searchViewType: matchedViewCode,
                }));
            });
    };

    return {
        ...store,

        // 초기 데이터에서 모든 카테고리 설정
        setAllCategoriesFromInitialData: (categories: AistudioAiCategoryWithTagDto[]) => store.update(state => ({ ...state, allCategoriesFromInitialData: categories })),

        // 선택된 탭의 태그 업데이트
        updateSelectedTagsByTab: (tabCode: string, tags: { value: string; label: string }[]) => {
            store.update(state => {
                const newMap = new Map(state.selectedTagsByTab);
                if (tags.length > 0) {
                    newMap.set(tabCode, tags);
                } else {
                    newMap.delete(tabCode);
                }
                return { ...state, selectedTagsByTab: newMap };
            });
        },

        // 카테고리 총계 맵 업데이트
        updateCategoryTotalsMap: (newMap: Map<string, number>) => store.update(state => ({ ...state, categoryTotalsMap: new Map(newMap) })),

        // 카테고리 총계 맵 병합 (병렬 executeSearch 호출 race-safe)
        // 초기 5개 탭 병렬 호출 시 각 호출이 snapshot 기반으로 replace 하는 대신
        // 최신 상태 위에 자기 카테고리 delta 만 얹는다.
        mergeCategoryTotals: (incoming: Map<string, number>) => {
            if (!incoming || incoming.size === 0) return;
            store.update(state => {
                const merged = new Map(state.categoryTotalsMap);
                let changed = false;
                incoming.forEach((value, key) => {
                    if (merged.get(key) !== value) {
                        merged.set(key, value);
                        changed = true;
                    }
                });
                if (!changed) return state;
                return { ...state, categoryTotalsMap: merged };
            });
        },

        // 탭 카운트 업데이트
        updateTabCounts: (newCounts: Map<string, number>) => store.update(state => ({ ...state, tabCounts: newCounts })),

        // 필터링된 카테고리 업데이트
        updateFilteredCategories: (selectedServiceTypes: ServiceType[], selectedCountry: SelectItem | null) => {
            store.update(state => {
                const filteredCategories = updateFilteredCategories(state.allCategoriesFromInitialData, selectedServiceTypes, selectedCountry);
                return { ...state, filteredCategories };
            });
        },

        // 뷰에서 API 카테고리 생성
        generateApiCategoriesForView,

        // 탭 카운트 계산
        calculateTabCounts: (availableViews: Array<{ code: string; filters: { serviceType: ServiceType; categoryType?: string } }>) => {
            const state = store.getState();
            const counts = new Map<string, number>();

            // 'all' 탭 총계 계산
            let allTabTotal = 0;
            const countedAllCategoryIds = new Set<string>();
            const allViewCategories = generateApiCategoriesForView('all', availableViews);
            for (const cat of allViewCategories) {
                const categoryId = String(cat.categoryId);
                if (countedAllCategoryIds.has(categoryId)) continue;
                countedAllCategoryIds.add(categoryId);
                allTabTotal += state.categoryTotalsMap.get(createSearchResultBucketKey(cat)) ?? state.categoryTotalsMap.get(cat.categoryId) ?? 0;
            }
            counts.set('all', allTabTotal);

            // 각 뷰별 탭 계산
            for (const view of availableViews) {
                let viewTotal = 0;
                const viewCategories = generateApiCategoriesForView(view.code, availableViews);
                for (const cat of viewCategories) {
                    viewTotal += state.categoryTotalsMap.get(createSearchResultBucketKey(cat)) ?? state.categoryTotalsMap.get(cat.categoryId) ?? 0;
                }
                counts.set(view.code, viewTotal);
            }

            return counts;
        },

        // 현재 선택된 탭의 태그 가져오기
        getSelectedTagsForCurrentTab: (selectedViewCode: string): { value: string; label: string }[] => {
            const state = store.getState();
            return state.selectedTagsByTab.get(selectedViewCode) || [];
        },

        // 탭의 태그 코드 가져오기
        getTagCodesForTab: (tabCode: string): string[] => {
            const state = store.getState();
            const tags = state.selectedTagsByTab.get(tabCode) || [];
            return tags.map(tag => tag.value);
        },

        // 실제 검색 요청에 사용할 탭별 태그 코드 가져오기
        getRequestTagCodesForView: (tabCode: string, availableViews: Array<{ code: string; filters: { serviceType: ServiceType; categoryType?: string } }>): string[] => {
            const state = store.getState();
            const explicitTags = (state.selectedTagsByTab.get(tabCode) || []).map(tag => tag.value);
            if (explicitTags.length > 0 || tabCode === 'all') {
                return explicitTags;
            }

            const view = availableViews.find(candidate => candidate.code === tabCode);
            if (!view) {
                return [];
            }

            return Array.from(
                new Set(
                    state.filteredCategories.flatMap(category => {
                        if (category.serviceType !== view.filters.serviceType) {
                            return [];
                        }

                        if (!matchesView(category, view)) {
                            return [];
                        }

                        return getDefaultRequestTagCodesForViewCode(tabCode, category.categoryType);
                    }),
                ),
            );
        },

        // 초기 검색 시작 시 prefetch 대상 탭 코드 집합을 설정 (전체 탭 spinner ON)
        setPendingTabCodes: (codes: string[]) => {
            store.update(state => ({ ...state, pendingTabCodes: new Set(codes) }));
        },

        // 특정 탭의 데이터 수신이 끝나면 해당 탭을 pending 에서 제거 (탭 spinner OFF)
        removePendingTabCode: (code: string) => {
            store.update(state => {
                if (!state.pendingTabCodes.has(code)) return state;
                const next = new Set(state.pendingTabCodes);
                next.delete(code);
                return { ...state, pendingTabCodes: next };
            });
        },

        // pending 탭 전체 해제 (abort 등)
        clearPendingTabCodes: () => {
            store.update(state => (state.pendingTabCodes.size === 0 ? state : { ...state, pendingTabCodes: new Set() }));
        },

        // 모든 탭 카운트 초기화
        resetTabCounts: () => {
            store.update(state => ({
                ...state,
                tabCounts: new Map(),
                categoryTotalsMap: new Map(),
            }));
        },

        // 스토어 초기화
        reset: () => store.set(initialState),
    };
};

// 싱글톤 인스턴스 생성 및 내보내기
export const searchCategoryStore = createCategoryStore();
